import type { AuthenticationRepository } from "~application/auth/ports/authentication-repository.ts";
import type { Tokens } from "~domain/auth/models/authentication-model.ts";
import type { Operator, OperatorAccesses } from "~domain/operators/models/operator-model.ts";
import type { BackofficeApi } from "~infrastructure/api/backoffice/http/backoffice-tsrest-client.ts";
import type { TAppConfig } from "~ui/@application/configs/config-schema.ts";

import { InvalidCredentialsError } from "~domain/auth/errors/auth-errors.ts";
import { DomainError, DomainErrorCode, DomainTransportError } from "~domain/global/errors/domain-error.ts";

export class BackofficeAuthenticationRepository implements AuthenticationRepository {
  constructor(
    private readonly appConfig: TAppConfig,
    private readonly backofficeClient: BackofficeApi,
  ) {}

  async login(username: string, password: string): Promise<Tokens> {
    if (this.appConfig.VITE_SHOULD_AUTHENTICATE_USING_CAPTCHA) {
      return this.loginWithCaptcha(username, password);
    }

    return this.loginWithoutCaptcha(username, password);
  }

  private async loginWithCaptcha(username: string, password: string): Promise<Tokens> {
    const token: string = await grecaptcha.enterprise.execute(this.appConfig.VITE_GOOGLE_CAPTCHA_SITE_KEY, {
      action: "login",
    });

    const response = await this.backofficeClient.authentication.loginWithCaptcha({
      body: { username: username, password: password },
      headers: {
        "x-captcha-client": this.appConfig.VITE_GOOGLE_CAPTCHA_SITE_KEY,
        "x-captcha-token": token,
      },
    });

    if (response.status === 200) {
      return {
        accessToken: response.body.access_token,
        refreshToken: response.body.refresh_token,
      };
    }

    if (response.status === 400) {
      throw new InvalidCredentialsError();
    }

    throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
  }

  private async loginWithoutCaptcha(username: string, password: string): Promise<Tokens> {
    const response = await this.backofficeClient.authentication.loginWithoutCaptcha({
      body: { username: username, password: password },
    });

    if (response.status === 200) {
      return {
        accessToken: response.body.access_token,
        refreshToken: response.body.refresh_token,
      };
    }

    throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.error_description);
  }

  async fetchAccessRights(): Promise<OperatorAccesses> {
    const response = await this.backofficeClient.authentication.getOperatorAccesses();

    if (response.status === 200) {
      return response.body;
    }

    throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.error_description);
  }

  async fetchMe(): Promise<Operator> {
    const response = await this.backofficeClient.authentication.getCurrentOperatorInfo();

    if (response.status === 200) {
      return response.body;
    }

    throw new DomainTransportError(DomainErrorCode.UNEXPECTED_ERROR);
  }

  async ping(): Promise<"ok" | "unauthorized"> {
    const response = await this.backofficeClient.others.hello();

    if (response.status === 200) {
      return "ok";
    }

    return "unauthorized";
  }
}
